// src/components/Header.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import appConfig from '../config/appConfig';
import { useBranding } from '../context/BrandingContext';
import { useSafeNavigate } from '../context/UnsavedChangesContext';
import BrandingLogo from './BrandingLogo';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import {
    isAuthenticated,
    getActiveRole,
    getRoles,
    targetPathForRole,
    setActiveRole,
    logoutAndRedirect,
} from '../utils/auth';
import { useTranslation } from 'react-i18next';
import { useMobileNav } from '../context/MobileNavContext';
import { useRegionalSettings } from '../context/RegionalSettingsContext';
import {
    applyUiLanguage,
    getUiLanguageLabel,
    normalizeUiLanguage,
    persistPreferredLanguage,
} from '../utils/uiLanguage';

const PROFILE_MENU_WIDTH = 240; // w-60

const Header = ({ showMenuButton = false }) => {
    const { i18n, t } = useTranslation();
    const navigate = useSafeNavigate();
    const location = useLocation();
    const { isOpen: navOpen, toggle: toggleNav } = useMobileNav();
    const { appName, tagline, ready, hydrated } = useBranding();
    const { settings: regionalSettings, ready: regionalReady } = useRegionalSettings();
    const enabledUiLanguages = Array.isArray(regionalSettings?.enabledUiLanguages)
        && regionalSettings.enabledUiLanguages.length
        ? regionalSettings.enabledUiLanguages
        : ['en'];
    const showLanguageSelect = regionalReady && enabledUiLanguages.length > 1;
    const showName = appName || (ready ? appConfig.appName : null);
    const rawTagline = (ready || hydrated) ? (tagline || (ready ? appConfig.tagline : '')) : null;
    const defaultEnTagline = 'Manage. Monitor. Grow.';
    const showTagline = rawTagline
        ? (rawTagline === defaultEnTagline || rawTagline === appConfig.tagline
            ? t('brand.tagline')
            : rawTagline)
        : null;
    const authed = isAuthenticated();
    const activeRole = getActiveRole();
    const roles = getRoles();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!regionalReady || enabledUiLanguages.length !== 1) return;
        const only = enabledUiLanguages[0];
        if (normalizeUiLanguage(i18n.language) !== only) {
            applyUiLanguage(only);
        }
    }, [regionalReady, enabledUiLanguages, i18n.language]);
    const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const backdropRef = useRef(null);

    const closeMenu = () => setOpen(false);

    const isInsideMenu = (target) => {
        if (!(target instanceof Node)) return false;
        if (triggerRef.current?.contains(target)) return true;
        if (menuRef.current?.contains(target)) return true;
        if (backdropRef.current?.contains(target)) return true;
        return false;
    };

    // Close profile menu on navigation or any route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const updateMenuPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const left = Math.min(
            Math.max(8, rect.right - PROFILE_MENU_WIDTH),
            window.innerWidth - PROFILE_MENU_WIDTH - 8,
        );
        setMenuStyle({
            top: rect.bottom + 12,
            left,
        });
    };

    useEffect(() => {
        if (!open) return;
        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);
        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [open]);

    // Close on any outside pointer/touch — capture phase so it runs before other handlers
    useLayoutEffect(() => {
        if (!open) return;

        const onPointerDown = (e) => {
            if (isInsideMenu(e.target)) return;
            closeMenu();
        };

        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open]);

    const profilePath =
        activeRole === 'ADMIN' ? '/admin/profile' :
            activeRole === 'MANAGER' ? '/manager/profile' :
                activeRole === 'COACH' ? '/coach/profile' :
                    activeRole === 'STUDENT' ? '/student/profile' :
                        activeRole === 'PARENT' ? '/parent/profile' :
                            '/login';

    const canSwitch = roles.length > 1;

    const handleLogout = () => {
        setOpen(false);
        logoutAndRedirect();
    };

    const handleSwitch = (role) => {
        setActiveRole(role);
        setOpen(false);
        navigate(targetPathForRole(role), { replace: true });
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            'ADMIN': 'bg-danger-500',
            'MANAGER': 'bg-primary-600',
            'COACH': 'bg-warning-500',
            'STUDENT': 'bg-success-500',
            'PARENT': 'bg-indigo-500',
        };
        return colors[role] || 'bg-gray-500';
    };

    const profileMenuPortal = open && typeof document !== 'undefined'
        ? createPortal(
            <>
                {/* Backdrop — catches clicks/taps on the rest of the page */}
                <div
                    ref={backdropRef}
                    className="fixed inset-0 z-[250]"
                    aria-hidden="true"
                    onPointerDown={closeMenu}
                />
                <div
                    ref={menuRef}
                    role="menu"
                    style={{ top: menuStyle.top, left: menuStyle.left, width: PROFILE_MENU_WIDTH }}
                    className="animate-fade-in fixed z-[251] overflow-hidden rounded-2xl border border-slate-200 bg-white text-gray-800 shadow-[0_18px_45px_rgba(15,23,42,0.16)]"
                >
                    <button
                        type="button"
                        onClick={() => { setOpen(false); navigate(profilePath); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                    >
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="text-sm font-medium">{t('header.myProfile')}</span>
                    </button>

                    {canSwitch && (
                        <>
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-t border-b border-gray-100">
                                {t('header.switchRole')}
                            </div>
                            {roles.map((r) => (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => handleSwitch(r)}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${r === activeRole ? 'bg-primary-50' : ''}`}
                                    role="menuitem"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className={`w-2 h-2 rounded-full ${getRoleBadgeColor(r)}`} />
                                        <span className={`text-sm ${r === activeRole ? 'font-semibold text-primary-700' : ''}`}>
                                            {t(`faq.roles.${r}`, { defaultValue: r })}
                                        </span>
                                    </div>
                                    {r === activeRole && (
                                        <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </>
                    )}

                    <div className="border-t border-gray-100">
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-3 text-danger-600 hover:bg-danger-50 transition-colors"
                            role="menuitem"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span className="text-sm font-medium">{t('header.logout')}</span>
                        </button>
                    </div>
                </div>
            </>,
            document.body,
        )
        : null;

    return (
        <>
        <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 text-white shadow-[0_12px_30px_rgba(2,6,23,0.45)] backdrop-blur-xl overflow-visible">
            <div className="px-4 py-3 sm:px-6 overflow-visible">
                <div className="flex items-center justify-between gap-2 overflow-visible">
                    {/* Left: hamburger (mobile) + logo + brand */}
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        {authed && showMenuButton && (
                            <button
                                type="button"
                                onClick={toggleNav}
                                className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-white transition-all hover:bg-white/20 active:scale-95 lg:hidden"
                                aria-label={navOpen ? t('header.closeNav') : t('header.openNav')}
                                aria-expanded={navOpen}
                                aria-controls="app-sidebar"
                            >
                                {navOpen ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        )}
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <div className="h-9 w-9 sm:h-14 sm:w-14 flex-shrink-0 rounded-xl sm:rounded-2xl bg-white/95 p-1 sm:p-1.5 shadow-[0_12px_28px_rgba(255,255,255,0.18)] transition-shadow hover:shadow-[0_16px_32px_rgba(255,255,255,0.22)]">
                                <BrandingLogo
                                    className="w-full h-full object-contain rounded-lg"
                                    boxClassName="w-full h-full rounded-lg"
                                />
                            </div>
                            <div className="min-w-0">
                                {showName ? (
                                    <h1 className="text-sm sm:text-xl font-bold tracking-tight text-white truncate">{showName}</h1>
                                ) : (
                                    <div className="h-5 sm:h-7 w-28 sm:w-40 rounded bg-white/20 animate-pulse" aria-hidden="true" />
                                )}
                                {showTagline ? (
                                    <p className="hidden text-xs font-medium text-slate-300 sm:block">{showTagline}</p>
                                ) : !ready && !hydrated ? (
                                    <div className="hidden sm:block h-3 w-24 mt-1 rounded bg-white/10 animate-pulse" aria-hidden="true" />
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 flex-1 justify-end min-w-0">
                        {showLanguageSelect && (
                        <label className="flex items-center">
                            <span className="sr-only">{t('common.language')}</span>
                            <select
                                aria-label={t('common.language')}
                                className="rounded-lg border border-white/20 bg-white/10 px-2 py-1 text-xs text-white"
                                value={normalizeUiLanguage(i18n.language || 'en')}
                                onChange={(e) => {
                                    const lng = applyUiLanguage(e.target.value);
                                    if (authed) {
                                        persistPreferredLanguage(lng).catch((err) => {
                                            console.warn('Failed to persist preferred language', err);
                                        });
                                    }
                                }}
                            >
                                {enabledUiLanguages.map((code) => (
                                    <option key={code} value={code} className="text-gray-900">
                                        {getUiLanguageLabel(code)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        )}
                        {authed && (
                            <>
                                {['ADMIN', 'MANAGER', 'COACH'].includes(activeRole) && <GlobalSearch />}
                                <NotificationBell />

                                <div className="hidden shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 backdrop-blur-sm sm:flex">
                                    <span className={`w-2 h-2 rounded-full ${getRoleBadgeColor(activeRole)}`} />
                                    <span className="text-sm font-medium uppercase tracking-wider">
                                        {activeRole
                                            ? t(`faq.roles.${activeRole}`, { defaultValue: activeRole })
                                            : t('common.system')}
                                    </span>
                                </div>

                                <div className="relative shrink-0" ref={triggerRef}>
                                    <button
                                        type="button"
                                        onClick={() => setOpen((o) => !o)}
                                        className={`flex items-center gap-2 rounded-2xl border px-3 py-2 backdrop-blur-sm transition-all duration-200 hover:bg-white/20 ${
                                            open
                                                ? 'border-white/30 bg-white/20'
                                                : 'border-white/10 bg-white/10'
                                        }`}
                                        aria-haspopup="menu"
                                        aria-expanded={open ? 'true' : 'false'}
                                        aria-label={t('header.accountMenu')}
                                    >
                                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <svg className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
        {profileMenuPortal}
        </>
    );
};

export default Header;
